<template>
    <div class="page-header">
        <div class="page-header-heading">
            <!-- A solver lead opened this page: the way back to the simulation, as the person left it. -->
            <v-btn v-if="returnToSolver" ref="backButton" variant="tonal" color="primary" size="small" prepend-icon="fa-arrow-left"
                class="page-header-back" @click="backToSolver">
                {{ translate('parcours.planning.solverCheck.backToSolver') }}
            </v-btn>
            <!-- The page scrolled to the field a lead opened: the same way back, floating, while this one is out of sight. -->
            <v-btn v-if="returnToSolver && backOutOfView" variant="flat" color="primary" size="small" prepend-icon="fa-arrow-left"
                class="page-header-back--floating" @click="backToSolver">
                {{ translate('parcours.planning.solverCheck.backToSolver') }}
            </v-btn>
            <div class="breadcrumb-help-wrap" v-help="helpKey">
                <BaseBreadcrumb :breadcrumbs="breadcrumbs" />
            </div>
            <!-- Not for the exam supervisor (#1164): their sheet is locked on the classic look. -->
            <div v-if="route.meta.teachingGridAppearance && !ctxStore.examSupervisorOnly" class="page-header-appearance">
                <TeachingGridDensity :key="String(route.name)" />
            </div>
        </div>

        <!-- Desktop: the context toolbar sits inline at the right of the header. -->
        <ContextToolbar v-if="!smAndDown" :highlight="currentRouteHighlight" />

        <!-- Mobile / tablet (#116): collapse the toolbar behind a filter button so it no
             longer eats vertical space above the page. Tapping it reveals the filters on
             their own full-width row; a dot marks that a filter is active while collapsed. -->
        <template v-else-if="hasFilters">
            <v-btn :variant="showFilters ? 'tonal' : 'text'" :color="showFilters ? 'primary' : undefined" icon
                size="small" :title="showFilters ? translate('parcours.page.hideFilters') : translate('parcours.page.filters')"
                @click="showFilters = !showFilters">
                <v-badge :model-value="hasActiveSelection && !showFilters" color="primary" dot>
                    <v-icon>fa-filter</v-icon>
                </v-badge>
            </v-btn>
            <v-expand-transition>
                <div v-if="showFilters" class="ctx-mobile-wrap">
                    <ContextToolbar :highlight="currentRouteHighlight" />
                </div>
            </v-expand-transition>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, type ComponentPublicInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import { BaseBreadcrumb } from '@fondationcss/ui';
import { translate } from '@fondationcss/sdk';
import ContextToolbar from '@/components/context-toolbar.vue';
import TeachingGridDensity from '@/components/teaching-grid-density.vue';
import { usePageHeaderStore } from '@/stores/page-header-store';
import { pageHeaderContent } from '@/composables/use-page-breadcrumbs';
import { useContextToolbarStore } from '@/stores/context-toolbar-store';
import { useSpecialistGroupStore } from '@/stores/specialist-group-store';
import { useSdk as useCoreSdk } from '@fondationcss/sdk';
import { visibleContextAxes } from '@/composables/context-axis';
import { useSolverSessionStore } from '@/stores/solver-session-store';

const store = usePageHeaderStore();
const ctxStore = useContextToolbarStore();
const specialistGroups = useSpecialistGroupStore();
const coreSdk = useCoreSdk();
const route = useRoute();
const { smAndDown } = useDisplay();

// Collapsed by default on mobile; toggled by the filter button.
const showFilters = ref(false);

// Same default as ContextToolbar — a route with no context dropdowns has nothing to collapse.
// The axes are the toolbar's own reading (visibleContextAxes): the Groupe axis follows the
// « Formation par groupes » flag (#812), and the exam supervisor gets none (#1164) — a route
// whose visible axes are all gone must not offer an empty filter panel.
const DEFAULT_CONTEXT_DROPDOWNS = ['site', 'program', 'group', 'teacher'];
const routeAxes = computed(() => (route.meta.contextToolbar as string[] | undefined) ?? DEFAULT_CONTEXT_DROPDOWNS);
// The specialist's « Groupe » (#1445) is drawn once their list holds a group: the bar's own rule.
const hasFilters = computed(() =>
    visibleContextAxes(routeAxes.value, coreSdk.Features.flags.value, ctxStore.examSupervisorOnly)
        .some(axis => axis !== 'specialistGroup' || specialistGroups.groups.length > 0));

// Drives the "active filter" dot so the user sees a context is applied while collapsed. The
// « Activité » filter (#1163) counts only where the axis is shown: it persists across sessions
// and hides rows, so a collapsed Présences panel has to say it is on.
const hasActiveSelection = computed(() =>
    !!(ctxStore.selectedSite || ctxStore.selectedProgram || ctxStore.selectedGroup || ctxStore.selectedTeacher
        || (routeAxes.value.includes('activity') && ctxStore.selectedActivityTypes.length > 0)
        || (routeAxes.value.includes('specialistGroup') && specialistGroups.selected))
);

const currentRouteHighlight = computed(() =>
    ctxStore.highlightRoute === (route.name ?? null) ? ctxStore.highlight : []
);

// A trail stamped by another route is stale — the page we are on never wrote it.
// Applies to routes that mount a Fondation component (the legal documents) or
// simply never call usePageBreadcrumbs; they used to inherit the previous page's
// trail and its help key (#715). The decision itself lives in the composable so a
// test can exercise the real thing instead of a copy of it.
const content = computed(() => pageHeaderContent(store, route));

const helpKey = computed(() => content.value.helpKey);
const breadcrumbs = computed(() => content.value.breadcrumbs);

// The way back to the solver lives on the page a lead opened, and on that page only: going anywhere
// else drops it — the solver's own entry in the menu still finds the simulation as it was.
const router = useRouter();
const solverSession = useSolverSessionStore();
watch(() => route.path, (path) => solverSession.arrivedAt(path), { immediate: true });
const returnToSolver = computed(() => solverSession.back?.path === route.path);
function backToSolver() {
    void router.push({ name: 'planification-simulation' });
}
// A lead lands on its field, often far down the page: the header's button then floats until it is back in sight.
const backButton = ref<ComponentPublicInstance | null>(null);
const backOutOfView = ref(false);
let backObserver: IntersectionObserver | undefined;
watch(() => backButton.value?.$el as Element | undefined, (el) => {
    backObserver?.disconnect();
    backOutOfView.value = false;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    backObserver = new IntersectionObserver(([entry]) => { backOutOfView.value = !entry.isIntersecting; });
    backObserver.observe(el);
});
onBeforeUnmount(() => backObserver?.disconnect());
</script>

<style scoped>
.page-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    flex-shrink: 0;
    min-height: 40px;
    gap: 16px;
}

.page-header-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

/* Floats at the foot of the content, clear of the navigation drawer, while the header is out of sight. */
.page-header-back--floating {
    position: fixed;
    bottom: calc(20px + env(safe-area-inset-bottom, 0px));
    left: calc(var(--v-layout-left, 0px) + 24px);
    z-index: 5;
    box-shadow: 0 6px 18px rgba(var(--v-theme-on-surface), 0.18);
}

.page-header-appearance {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
}

/* v-help appends its `?` trigger as a child of this element. Keeping it
   inline-flex lays the trigger out beside the breadcrumb instead of below it. */
.breadcrumb-help-wrap {
    display: flex;
    align-items: center;
}

/* BaseBreadcrumb's bare variant ships a 10px bottom margin for when it sits
   alone above content. In this flex row the margin box is what gets centered,
   which pushed the appended `?` trigger above the trail's centerline. */
.breadcrumb-help-wrap :deep(.base-breadcrumb) {
    margin-bottom: 0;
}

/* From 1280 px the header stays ONE row, whatever is selected (#1163): the bar's menus
   shrink instead. Wrapping dropped the whole bar under the breadcrumb, and pushed the page
   down, the moment a longer name was picked. Narrower screens keep the wrap — the bar then
   sits on its own row whatever is selected, its width no longer following the labels. */
@media (min-width: 1280px) {
    .page-header {
        flex-wrap: nowrap;
    }

    .page-header-heading {
        flex-shrink: 0;
    }
}

/* Mobile / tablet (#116): the breadcrumb keeps the first row with the filter button at
   its right (via space-between); the revealed toolbar wraps onto its own full-width row. */
@media (max-width: 959px) {
    .page-header {
        flex-wrap: wrap;
        gap: 8px;
    }

    .ctx-mobile-wrap {
        flex: 1 1 100%;
    }
}
</style>
